import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
  Fade,
  Grow,
  Zoom,
  Slide,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Stack,
} from '@mui/material';
import { Save, Email, WhatsApp, Public, Schedule, Timer, UploadFile, Delete, Refresh, CheckCircle, CloudSync, Settings as SettingsIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';

// List of all timezones
const TIMEZONES = [
  { value: 'Pacific/Midway', label: '(UTC-11:00) Midway Island' },
  { value: 'Pacific/Samoa', label: '(UTC-11:00) Samoa' },
  { value: 'Pacific/Honolulu', label: '(UTC-10:00) Hawaii' },
  { value: 'America/Anchorage', label: '(UTC-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: '(UTC-07:00) Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: '(UTC-06:00) Central Time (US & Canada)' },
  { value: 'America/New_York', label: '(UTC-05:00) Eastern Time (US & Canada)' },
  { value: 'America/Caracas', label: '(UTC-04:00) Caracas' },
  { value: 'America/Sao_Paulo', label: '(UTC-03:00) Sao Paulo' },
  { value: 'Atlantic/South_Georgia', label: '(UTC-02:00) Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: '(UTC-01:00) Azores' },
  { value: 'Europe/London', label: '(UTC+00:00) London, Dublin, Lisbon' },
  { value: 'Europe/Paris', label: '(UTC+01:00) Paris, Berlin, Rome' },
  { value: 'Europe/Athens', label: '(UTC+02:00) Athens, Cairo, Helsinki' },
  { value: 'Europe/Moscow', label: '(UTC+03:00) Moscow, Baghdad' },
  { value: 'Asia/Dubai', label: '(UTC+04:00) Dubai, Muscat' },
  { value: 'Asia/Karachi', label: '(UTC+05:00) Karachi, Tashkent' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Mumbai, New Delhi, Colombo' },
  { value: 'Asia/Dhaka', label: '(UTC+06:00) Dhaka' },
  { value: 'Asia/Bangkok', label: '(UTC+07:00) Bangkok, Jakarta' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Singapore, Hong Kong' },
  { value: 'Asia/Shanghai', label: '(UTC+08:00) Beijing, Chongqing' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo, Seoul' },
  { value: 'Asia/Seoul', label: '(UTC+09:00) Seoul' },
  { value: 'Australia/Darwin', label: '(UTC+09:30) Darwin' },
  { value: 'Australia/Sydney', label: '(UTC+10:00) Sydney, Melbourne' },
  { value: 'Pacific/Guam', label: '(UTC+10:00) Guam' },
  { value: 'Asia/Magadan', label: '(UTC+11:00) Magadan' },
  { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, Wellington' },
  { value: 'Pacific/Fiji', label: '(UTC+12:00) Fiji' },
];

const Settings = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const isAdmin = user?.role === 'admin' || user?.role === 'hr';
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Email Alert Settings
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailLogin, setEmailLogin] = useState(false);
  const [emailLogout, setEmailLogout] = useState(false);
  const [emailBreakStart, setEmailBreakStart] = useState(false);
  const [emailBreakEnd, setEmailBreakEnd] = useState(false);
  const [emailUserCreated, setEmailUserCreated] = useState(false);
  const [emailDayEndReport, setEmailDayEndReport] = useState(false);
  
  // SMTP Settings
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [hrEmail, setHrEmail] = useState('');
  
  // WhatsApp Alert Settings
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappLogin, setWhatsappLogin] = useState(false);
  const [whatsappLogout, setWhatsappLogout] = useState(false);
  const [whatsappBreakStart, setWhatsappBreakStart] = useState(false);
  const [whatsappBreakEnd, setWhatsappBreakEnd] = useState(false);
  const [whatsappUserCreated, setWhatsappUserCreated] = useState(false);
  const [whatsappDayEndReport, setWhatsappDayEndReport] = useState(false);
  
  // WhatsApp API Settings
  const [whatsappApiUrl, setWhatsappApiUrl] = useState('');
  const [whatsappApiKey, setWhatsappApiKey] = useState('');
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState('');

  // Timezone Settings
  const [selectedTimezone, setSelectedTimezone] = useState('Asia/Kolkata');

  // General Settings
  const [holidayLimit, setHolidayLimit] = useState(10);

  // Overtime Settings
  const [overtimeEnabled, setOvertimeEnabled] = useState(false);
  const [overtimeRate, setOvertimeRate] = useState(1.5);
  const [minimumOvertimeHours, setMinimumOvertimeHours] = useState(1);
  const [dailyOvertimeLimit, setDailyOvertimeLimit] = useState(4);
  const [weeklyOvertimeLimit, setWeeklyOvertimeLimit] = useState(20);
  const [overtimeCalculationMethod, setOvertimeCalculationMethod] = useState('daily');

  // Late Arrival & Early Departure Settings
  const [lateArrivalGracePeriod, setLateArrivalGracePeriod] = useState(15);
  const [earlyDepartureGracePeriod, setEarlyDepartureGracePeriod] = useState(15);
  const [lateArrivalDeduction, setLateArrivalDeduction] = useState(false);
  const [earlyDepartureDeduction, setEarlyDepartureDeduction] = useState(false);

  // Break Time Settings
  const [maxBreakDuration, setMaxBreakDuration] = useState(60);
  const [breakTimeDeduction, setBreakTimeDeduction] = useState(false);

  // Face Verification Settings
  const [faceVerificationEnabled, setFaceVerificationEnabled] = useState(true);
  const [faceVerificationRequired, setFaceVerificationRequired] = useState(false);
  const [faceVerificationTolerance, setFaceVerificationTolerance] = useState(0.6);
  
  // Logo Settings
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [currentLogo, setCurrentLogo] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoSaved, setLogoSaved] = useState(false);

  // Google Sheets Integration Settings
  const [googleSheetsEnabled, setGoogleSheetsEnabled] = useState(false);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [googleAppsScriptUrl, setGoogleAppsScriptUrl] = useState('');
  const [googleSheetId, setGoogleSheetId] = useState('');
  const [googlePayslipSheetId, setGooglePayslipSheetId] = useState('');
  const [googleServiceAccountEmail, setGoogleServiceAccountEmail] = useState('');
  const [googlePrivateKey, setGooglePrivateKey] = useState('');
  const [dailyAttendanceSync, setDailyAttendanceSync] = useState(true);
  const [monthlyPayslipSync, setMonthlyPayslipSync] = useState(true);
  const [syncTime, setSyncTime] = useState('18:00');
  const [realtimeSync, setRealtimeSync] = useState(false);
  const [autoAbsentEnabled, setAutoAbsentEnabled] = useState(false);
  const [autoAbsentTime, setAutoAbsentTime] = useState('18:30');
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAlertConfig();
      if (response.data.success) {
        const alerts = response.data.data;
        
        setEmailEnabled(alerts.email.enabled);
        setEmailLogin(alerts.email.login);
        setEmailLogout(alerts.email.logout);
        setEmailBreakStart(alerts.email.break_start);
        setEmailBreakEnd(alerts.email.break_end);
        setEmailUserCreated(alerts.email.user_created);
        setEmailDayEndReport(alerts.email.day_end_report);
        setSmtpHost(alerts.email.smtp_host);
        setSmtpPort(alerts.email.smtp_port);
        setSmtpUser(alerts.email.smtp_user);
        setSmtpPassword(alerts.email.smtp_password);
        setSmtpFromEmail(alerts.email.smtp_from_email);
        setSmtpFromName(alerts.email.smtp_from_name);
        setAdminEmail(alerts.email.admin_email || '');
        setHrEmail(alerts.email.hr_email || '');
        
        setWhatsappEnabled(alerts.whatsapp.enabled);
        setWhatsappLogin(alerts.whatsapp.login);
        setWhatsappLogout(alerts.whatsapp.logout);
        setWhatsappBreakStart(alerts.whatsapp.break_start);
        setWhatsappBreakEnd(alerts.whatsapp.break_end);
        setWhatsappUserCreated(alerts.whatsapp.user_created);
        setWhatsappDayEndReport(alerts.whatsapp.day_end_report);
        setWhatsappApiUrl(alerts.whatsapp.api_url);
        setWhatsappApiKey(alerts.whatsapp.api_key);
        setWhatsappPhoneNumber(alerts.whatsapp.phone_number);
      }
      
      // Load timezone
      try {
        const tzResponse = await settingsAPI.getByKey('timezone');
        if (tzResponse.data.success && tzResponse.data.data) {
          setSelectedTimezone(tzResponse.data.data.value || 'Asia/Kolkata');
        }
      } catch (tzError) {
        console.log('Timezone not set yet');
      }
      
      // Load general settings
      try {
        const [holidayLimitRes] = await Promise.all([
          settingsAPI.getByKey('optional_holiday_limit'),
        ]);

        if (holidayLimitRes.data.success && holidayLimitRes.data.data) {
          setHolidayLimit(parseInt(holidayLimitRes.data.data.value) || 10);
        }
      } catch (wsError) {
        console.log('Settings not set yet');
      }

      // Load overtime settings
      try {
        const [overtimeEnabledRes, overtimeRateRes, minOvertimeRes, dailyLimitRes, weeklyLimitRes, calcMethodRes] = await Promise.all([
          settingsAPI.getByKey('overtime_enabled'),
          settingsAPI.getByKey('overtime_rate'),
          settingsAPI.getByKey('minimum_overtime_hours'),
          settingsAPI.getByKey('daily_overtime_limit'),
          settingsAPI.getByKey('weekly_overtime_limit'),
          settingsAPI.getByKey('overtime_calculation_method'),
        ]);
        
        if (overtimeEnabledRes.data.success && overtimeEnabledRes.data.data) {
          setOvertimeEnabled(overtimeEnabledRes.data.data.value === 'true');
        }
        if (overtimeRateRes.data.success && overtimeRateRes.data.data) {
          setOvertimeRate(parseFloat(overtimeRateRes.data.data.value) || 1.5);
        }
        if (minOvertimeRes.data.success && minOvertimeRes.data.data) {
          setMinimumOvertimeHours(parseFloat(minOvertimeRes.data.data.value) || 1);
        }
        if (dailyLimitRes.data.success && dailyLimitRes.data.data) {
          setDailyOvertimeLimit(parseFloat(dailyLimitRes.data.data.value) || 4);
        }
        if (weeklyLimitRes.data.success && weeklyLimitRes.data.data) {
          setWeeklyOvertimeLimit(parseFloat(weeklyLimitRes.data.data.value) || 20);
        }
        if (calcMethodRes.data.success && calcMethodRes.data.data) {
          setOvertimeCalculationMethod(calcMethodRes.data.data.value || 'daily');
        }
      } catch (otError) {
        console.log('Overtime settings not set yet');
      }

      // Load attendance settings
      try {
        const [lateGraceRes, earlyGraceRes, lateDeductRes, earlyDeductRes] = await Promise.all([
          settingsAPI.getByKey('late_arrival_grace_period'),
          settingsAPI.getByKey('early_departure_grace_period'),
          settingsAPI.getByKey('late_arrival_deduction'),
          settingsAPI.getByKey('early_departure_deduction'),
        ]);
        
        if (lateGraceRes.data.success && lateGraceRes.data.data) {
          setLateArrivalGracePeriod(parseInt(lateGraceRes.data.data.value) || 15);
        }
        if (earlyGraceRes.data.success && earlyGraceRes.data.data) {
          setEarlyDepartureGracePeriod(parseInt(earlyGraceRes.data.data.value) || 15);
        }
        if (lateDeductRes.data.success && lateDeductRes.data.data) {
          setLateArrivalDeduction(lateDeductRes.data.data.value === 'true');
        }
        if (earlyDeductRes.data.success && earlyDeductRes.data.data) {
          setEarlyDepartureDeduction(earlyDeductRes.data.data.value === 'true');
        }
      } catch (attError) {
        console.log('Attendance settings not set yet');
      }

      // Load break settings
      try {
        const [maxBreakRes, breakDeductRes] = await Promise.all([
          settingsAPI.getByKey('max_break_duration'),
          settingsAPI.getByKey('break_time_deduction'),
        ]);
        
        if (maxBreakRes.data.success && maxBreakRes.data.data) {
          setMaxBreakDuration(parseInt(maxBreakRes.data.data.value) || 60);
        }
        if (breakDeductRes.data.success && breakDeductRes.data.data) {
          setBreakTimeDeduction(breakDeductRes.data.data.value === 'true');
        }
      } catch (breakError) {
        console.log('Break settings not set yet');
      }

      // Load face verification settings
      try {
        const [faceEnabledRes, faceRequiredRes, faceToleranceRes] = await Promise.all([
          settingsAPI.getByKey('face_verification_enabled'),
          settingsAPI.getByKey('face_verification_required'),
          settingsAPI.getByKey('face_verification_tolerance'),
        ]);
        
        if (faceEnabledRes.data.success && faceEnabledRes.data.data) {
          setFaceVerificationEnabled(faceEnabledRes.data.data.value === 'true');
        }
        if (faceRequiredRes.data.success && faceRequiredRes.data.data) {
          setFaceVerificationRequired(faceRequiredRes.data.data.value === 'true');
        }
        if (faceToleranceRes.data.success && faceToleranceRes.data.data) {
          setFaceVerificationTolerance(parseFloat(faceToleranceRes.data.data.value) || 0.6);
        }
      } catch (faceError) {
        console.log('Face verification settings not set yet');
      }
      
      // Load logo settings
      try {
        const logoRes = await settingsAPI.getByKey('logo_url');
        if (logoRes.data.success && logoRes.data.data) {
          const logoUrl = logoRes.data.data.value;
          if (logoUrl) {
            setCurrentLogo(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5076'}${logoUrl}`);
          }
        }
      } catch (logoError) {
        console.log('Logo settings not set yet');
      }

      // Load Google Sheets integration settings
      try {
        const gsResponse = await settingsAPI.getGoogleSheetsConfig();
        if (gsResponse.data.success && gsResponse.data.data) {
          const gs = gsResponse.data.data;
          setGoogleSheetsEnabled(gs.enabled || false);
          setGoogleSheetUrl(gs.sheet_url || '');
          setGoogleAppsScriptUrl(gs.apps_script_url || '');
          setGoogleSheetId(gs.sheet_id || '');
          setGooglePayslipSheetId(gs.payslip_sheet_id || '');
          setGoogleServiceAccountEmail(gs.service_account_email || '');
          setGooglePrivateKey(gs.private_key || '');
          setDailyAttendanceSync(gs.daily_attendance_sync !== false);
          setMonthlyPayslipSync(gs.monthly_payslip_sync !== false);
          setSyncTime(gs.sync_time || '18:00');
          setRealtimeSync(gs.realtime_sync || false);
          setAutoAbsentEnabled(gs.auto_absent_enabled || false);
          setAutoAbsentTime(gs.auto_absent_time || '18:30');
          setLastSyncStatus(gs.last_sync || null);
        }
      } catch (gsError) {
        console.log('Google Sheets settings not configured');
      }

    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const showSuccessMessage = (text) => {
    setSaveSuccess(true);
    setMessage({ type: 'success', text });
    setTimeout(() => setSaveSuccess(false), 3000);
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const saveEmailSettings = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const alerts = {
        enabled: emailEnabled,
        login: emailLogin,
        logout: emailLogout,
        break_start: emailBreakStart,
        break_end: emailBreakEnd,
        user_created: emailUserCreated,
        day_end_report: emailDayEndReport,
        smtp_host: smtpHost,
        smtp_port: smtpPort,
        smtp_user: smtpUser,
        smtp_password: smtpPassword === '***HIDDEN***' ? '' : smtpPassword,
        smtp_from_email: smtpFromEmail,
        smtp_from_name: smtpFromName,
        admin_email: adminEmail,
        hr_email: hrEmail,
      };

      const response = await settingsAPI.saveAlertConfig(alerts, 'email');

      if (response.data.success) {
        showSuccessMessage('Email settings saved successfully!');
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to save email settings' });
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save email settings';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const testData = {
        to: smtpUser,
        subject: 'Test Email from Attendance System',
        message: 'This is a test email to verify SMTP configuration.'
      };

      const response = await settingsAPI.sendTestEmail(testData);

      if (response.data.success) {
        showSuccessMessage('Test email sent successfully! Check your inbox (including spam folder).');
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to send test email' });
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to send test email';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const saveWhatsappSettings = async () => {
    try {
      setSaving(true);
      const alerts = {
        enabled: whatsappEnabled,
        login: whatsappLogin,
        logout: whatsappLogout,
        break_start: whatsappBreakStart,
        break_end: whatsappBreakEnd,
        user_created: whatsappUserCreated,
        day_end_report: whatsappDayEndReport,
        api_url: whatsappApiUrl,
        api_key: whatsappApiKey,
        phone_number: whatsappPhoneNumber,
      };
      
      await settingsAPI.saveAlertConfig(alerts, 'whatsapp');
      showSuccessMessage('WhatsApp settings saved successfully!');
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
      setMessage({ type: 'error', text: 'Failed to save WhatsApp settings' });
    } finally {
      setSaving(false);
    }
  };

  const saveTimezoneSettings = async () => {
    try {
      setSaving(true);
      await settingsAPI.save({
        key: 'timezone',
        value: selectedTimezone,
        category: 'timezone',
        description: 'System timezone setting'
      });
      showSuccessMessage('Timezone saved successfully!');
    } catch (error) {
      console.error('Error saving timezone:', error);
      setMessage({ type: 'error', text: 'Failed to save timezone' });
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await settingsAPI.save({
        key: 'optional_holiday_limit',
        value: holidayLimit.toString(),
        category: 'general',
        description: 'Maximum number of optional holidays an employee can select per year'
      });
      showSuccessMessage('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const saveOvertimeSettings = async () => {
    try {
      setSaving(true);
      await settingsAPI.save({
        key: 'overtime_enabled',
        value: overtimeEnabled.toString(),
        category: 'overtime',
        description: 'Enable overtime calculation'
      });
      await settingsAPI.save({
        key: 'overtime_rate',
        value: overtimeRate.toString(),
        category: 'overtime',
        description: 'Overtime pay rate multiplier'
      });
      await settingsAPI.save({
        key: 'minimum_overtime_hours',
        value: minimumOvertimeHours.toString(),
        category: 'overtime',
        description: 'Minimum hours to qualify for overtime'
      });
      await settingsAPI.save({
        key: 'daily_overtime_limit',
        value: dailyOvertimeLimit.toString(),
        category: 'overtime',
        description: 'Maximum overtime hours per day'
      });
      await settingsAPI.save({
        key: 'weekly_overtime_limit',
        value: weeklyOvertimeLimit.toString(),
        category: 'overtime',
        description: 'Maximum overtime hours per week'
      });
      await settingsAPI.save({
        key: 'overtime_calculation_method',
        value: overtimeCalculationMethod,
        category: 'overtime',
        description: 'Overtime calculation method (daily or weekly)'
      });
      showSuccessMessage('Overtime settings saved successfully!');
    } catch (error) {
      console.error('Error saving overtime settings:', error);
      setMessage({ type: 'error', text: 'Failed to save overtime settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setLogoFile(file);
          setLogoPreview(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setMessage({ type: 'error', text: 'Please select an image file' });
      }
    }
  };

  const saveLogo = async () => {
    if (!logoFile) {
      setMessage({ type: 'error', text: 'Please select a logo to upload' });
      return;
    }

    try {
      setLogoLoading(true);
      
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await settingsAPI.uploadLogo(formData);
      
      if (response.data.success) {
        await settingsAPI.save({
          key: 'logo_url',
          value: response.data.data.logoUrl,
          category: 'general',
          description: 'Company logo URL'
        });
        
        setCurrentLogo(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5076'}${response.data.data.logoUrl}`);
        setLogoFile(null);
        setLogoPreview('');
        setLogoSaved(true);
        showSuccessMessage('Logo uploaded and saved successfully!');
      } else {
        setMessage({ type: 'error', text: 'Failed to upload logo' });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    } finally {
      setLogoLoading(false);
    }
  };

  const deleteLogo = async () => {
    try {
      setLogoLoading(true);
      
      const response = await settingsAPI.deleteLogo();
      
      if (response.data.success) {
        setCurrentLogo('');
        setLogoFile(null);
        setLogoPreview('');
        setLogoSaved(false);
        showSuccessMessage('Logo deleted successfully!');
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to delete logo' });
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      setMessage({ type: 'error', text: 'Failed to delete logo' });
    } finally {
      setLogoLoading(false);
    }
  };

  const resetLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setLogoSaved(false);
  };

  const saveAttendanceSettings = async () => {
    try {
      setSaving(true);
      await settingsAPI.save({
        key: 'late_arrival_grace_period',
        value: lateArrivalGracePeriod.toString(),
        category: 'attendance',
        description: 'Grace period for late arrival in minutes'
      });
      await settingsAPI.save({
        key: 'early_departure_grace_period',
        value: earlyDepartureGracePeriod.toString(),
        category: 'attendance',
        description: 'Grace period for early departure in minutes'
      });
      await settingsAPI.save({
        key: 'late_arrival_deduction',
        value: lateArrivalDeduction.toString(),
        category: 'attendance',
        description: 'Deduct salary for late arrival'
      });
      await settingsAPI.save({
        key: 'early_departure_deduction',
        value: earlyDepartureDeduction.toString(),
        category: 'attendance',
        description: 'Deduct salary for early departure'
      });
      showSuccessMessage('Attendance settings saved successfully!');
    } catch (error) {
      console.error('Error saving attendance settings:', error);
      setMessage({ type: 'error', text: 'Failed to save attendance settings' });
    } finally {
      setSaving(false);
    }
  };

  const saveBreakSettings = async () => {
    try {
      setSaving(true);
      await settingsAPI.save({
        key: 'max_break_duration',
        value: maxBreakDuration.toString(),
        category: 'break',
        description: 'Maximum break duration in minutes'
      });
      await settingsAPI.save({
        key: 'break_time_deduction',
        value: breakTimeDeduction.toString(),
        category: 'break',
        description: 'Deduct break time from working hours'
      });
      showSuccessMessage('Break settings saved successfully!');
    } catch (error) {
      console.error('Error saving break settings:', error);
      setMessage({ type: 'error', text: 'Failed to save break settings' });
    } finally {
      setSaving(false);
    }
  };

  const saveFaceVerificationSettings = async () => {
    try {
      setSaving(true);
      await settingsAPI.save({
        key: 'face_verification_enabled',
        value: faceVerificationEnabled.toString(),
        category: 'face_verification',
        description: 'Enable face verification for attendance'
      });
      await settingsAPI.save({
        key: 'face_verification_required',
        value: faceVerificationRequired.toString(),
        category: 'face_verification',
        description: 'Require face verification for all attendance'
      });
      await settingsAPI.save({
        key: 'face_verification_tolerance',
        value: faceVerificationTolerance.toString(),
        category: 'face_verification',
        description: 'Face verification tolerance level'
      });
      showSuccessMessage('Face verification settings saved successfully!');
    } catch (error) {
      console.error('Error saving face verification settings:', error);
      setMessage({ type: 'error', text: 'Failed to save face verification settings' });
    } finally {
      setSaving(false);
    }
  };

  const validatePrivateKeyFormat = (key) => {
    if (!key || key.trim() === '') return true; // Allow empty for Apps Script fallback
    const hasBegin = key.includes('-----BEGIN PRIVATE KEY-----');
    const hasEnd = key.includes('-----END PRIVATE KEY-----');
    return hasBegin && hasEnd;
  };

  const saveGoogleSheetsSettings = async () => {
    try {
      setSaving(true);
      
      // Validate private key format before saving
      if (!validatePrivateKeyFormat(googlePrivateKey)) {
        setSaving(false);
        setMessage({ type: 'error', text: 'Invalid private key format. Make sure to include the full key with "-----BEGIN PRIVATE KEY-----" and "-----END PRIVATE KEY-----" headers.' });
        return;
      }
      
      await settingsAPI.saveGoogleSheetsConfig({
        enabled: googleSheetsEnabled,
        sheet_url: googleSheetUrl,
        apps_script_url: googleAppsScriptUrl,
        sheet_id: googleSheetId,
        payslip_sheet_id: googlePayslipSheetId,
        service_account_email: googleServiceAccountEmail,
        private_key: googlePrivateKey,
        daily_attendance_sync: dailyAttendanceSync,
        monthly_payslip_sync: monthlyPayslipSync,
        sync_time: syncTime,
        realtime_sync: realtimeSync,
        auto_absent_enabled: autoAbsentEnabled,
        auto_absent_time: autoAbsentTime,
      });
      
      // Reschedule daily sync and auto-absent with new settings
      try {
        await settingsAPI.rescheduleSync();
      } catch (rescheduleErr) {
        console.log('Reschedule warning (non-critical):', rescheduleErr);
      }
      
      showSuccessMessage('Google Sheets settings saved and sync rescheduled!');
    } catch (error) {
      console.error('Error saving Google Sheets settings:', error);
      setMessage({ type: 'error', text: 'Failed to save Google Sheets settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async (type) => {
    try {
      setSyncing(true);
      const response = await settingsAPI.syncToGoogleSheets(type);
      if (response.data.success) {
        setLastSyncStatus(response.data.data);
        showSuccessMessage(`${type === 'attendance' ? 'Attendance' : 'Payslip'} data synced to Google Sheets successfully!`);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Sync failed' });
      }
    } catch (error) {
      console.error('Error syncing to Google Sheets:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to sync data to Google Sheets';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0A1929' }}>
        <Zoom in={true}>
          <CircularProgress sx={{ color: '#D4AF37' }} />
        </Zoom>
      </Box>
    );
  }

  if (user?.role === 'employee') {
    return (
      <Fade in={true}>
        <Box sx={{ p: 3, bgcolor: '#0A1929', minHeight: '100vh' }}>
          <Alert severity="warning" sx={{ bgcolor: '#fff3e0', color: '#856404', '& .MuiAlert-icon': { color: '#D4AF37' } }}>
            You don't have permission to access settings. Contact your administrator.
          </Alert>
        </Box>
      </Fade>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#0A1929', minHeight: '100vh' }}>
      <Grow in={true} timeout={500}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <SettingsIcon sx={{ fontSize: 32, color: '#D4AF37' }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: 'rgb(212, 175, 55);',
              letterSpacing: '-0.5px',
            }}
          >
            Settings & Alerts Configuration
          </Typography>
        </Stack>
      </Grow>
      
      {message.text && (
        <Slide direction="down" in={true} mountOnEnter unmountOnExit>
          <Alert 
            severity={message.type} 
            onClose={() => setMessage({ type: '', text: '' })}
            icon={message.type === 'success' ? <CheckCircle /> : undefined}
            sx={{ 
              mb: 3,
              bgcolor: message.type === 'success' ? '#e8f5e9' : '#ffebee',
              color: message.type === 'success' ? '#2e7d32' : '#c62828',
              '& .MuiAlert-icon': { color: message.type === 'success' ? '#D4AF37' : '#f44336' },
              borderLeft: `4px solid ${message.type === 'success' ? '#D4AF37' : '#f44336'}`,
            }}
          >
            {message.text}
          </Alert>
        </Slide>
      )}
      
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            bgcolor: '#0A1929',
            '& .MuiTab-root': { 
              color: '#B4C4A8',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              py: 2,
              '&:hover': { color: '#D4AF37' }
            },
            '& .Mui-selected': { 
              color: '#D4AF37',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': { 
              backgroundColor: '#D4AF37',
              height: 3,
            },
          }}
        >
          <Tab icon={<Email />} label="Email Alerts" iconPosition="start" />
          <Tab icon={<WhatsApp />} label="WhatsApp Alerts" iconPosition="start" />
          <Tab icon={<Public />} label="Timezone" iconPosition="start" />
          <Tab icon={<Schedule />} label="Work Schedule" iconPosition="start" />
          <Tab icon={<Timer />} label="Overtime" iconPosition="start" />
          <Tab icon={<UploadFile />} label="Logo" iconPosition="start" />
          <Tab icon={<CloudSync />} label="Google Sheets" iconPosition="start" />
        </Tabs>
      </Paper>
      
      {/* Email Alerts Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: '#0A1929', border: '1px solid #D4AF37', borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  SMTP Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMTP Host"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      disabled={!emailEnabled}
                      placeholder="smtp.gmail.com"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMTP Port"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                      disabled={!emailEnabled}
                      placeholder="587"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="SMTP Username"
                      value={smtpUser}
                      onChange={(e) => setSmtpUser(e.target.value)}
                      disabled={!emailEnabled}
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="password"
                      label="SMTP Password"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      disabled={!emailEnabled}
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="From Email"
                      value={smtpFromEmail}
                      onChange={(e) => setSmtpFromEmail(e.target.value)}
                      disabled={!emailEnabled}
                      placeholder="noreply@company.com"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="From Name"
                      value={smtpFromName}
                      onChange={(e) => setSmtpFromName(e.target.value)}
                      disabled={!emailEnabled}
                      placeholder="Attendance System"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Admin Email (comma-separated)"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      disabled={!emailEnabled}
                      placeholder="admin@company.com"
                      helperText="Email addresses to receive admin alerts (comma-separated for multiple)"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        },
                        '& .MuiFormHelperText-root': { color: '#D4AF37' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="HR Email (comma-separated)"
                      value={hrEmail}
                      onChange={(e) => setHrEmail(e.target.value)}
                      disabled={!emailEnabled}
                      placeholder="hr@company.com"
                      helperText="Email addresses to receive HR alerts (comma-separated for multiple)"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        },
                        '& .MuiFormHelperText-root': { color: '#D4AF37' }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Email Notifications
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailEnabled}
                      onChange={(e) => setEmailEnabled(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Enable Email Alerts</Typography>}
                />

                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />

                <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Alert Types
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailLogin}
                          onChange={(e) => setEmailLogin(e.target.checked)}
                          disabled={!emailEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Login Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailLogout}
                          onChange={(e) => setEmailLogout(e.target.checked)}
                          disabled={!emailEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Logout Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailBreakStart}
                          onChange={(e) => setEmailBreakStart(e.target.checked)}
                          disabled={!emailEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Break Start Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailBreakEnd}
                          onChange={(e) => setEmailBreakEnd(e.target.checked)}
                          disabled={!emailEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Break End Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailUserCreated}
                          onChange={(e) => setEmailUserCreated(e.target.checked)}
                          disabled={!emailEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>User Created Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailDayEndReport}
                          onChange={(e) => setEmailDayEndReport(e.target.checked)}
                          disabled={!emailEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Daily End Report</Typography>}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-start">
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
                onClick={saveEmailSettings}
                disabled={saving || !emailEnabled}
                sx={{
                  bgcolor: '#D4AF37',
                  color: '#0A1929',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: '#C19B2E' },
                }}
              >
                {saving ? 'Saving...' : 'Save Email Settings'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Email />}
                onClick={sendTestEmail}
                disabled={saving || !emailEnabled}
                sx={{
                  borderColor: '#D4AF37',
                  color: '#D4AF37',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' },
                }}
              >
                Send Test Email
              </Button>
            </Stack>
          </Grid>
        </Grid>
      )}
      
      {/* WhatsApp Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  WhatsApp Notifications
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={whatsappEnabled}
                      onChange={(e) => setWhatsappEnabled(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Enable WhatsApp Alerts</Typography>}
                />
                
                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />
                
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Alert Types
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={whatsappLogin}
                          onChange={(e) => setWhatsappLogin(e.target.checked)}
                          disabled={!whatsappEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Login Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={whatsappLogout}
                          onChange={(e) => setWhatsappLogout(e.target.checked)}
                          disabled={!whatsappEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Logout Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsappBreakStart}
                          onChange={(e) => setWhatsappBreakStart(e.target.checked)}
                          disabled={!whatsappEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Break Start Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsappBreakEnd}
                          onChange={(e) => setWhatsappBreakEnd(e.target.checked)}
                          disabled={!whatsappEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Break End Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={whatsappUserCreated}
                          onChange={(e) => setWhatsappUserCreated(e.target.checked)}
                          disabled={!whatsappEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>User Created Alert</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={whatsappDayEndReport}
                          onChange={(e) => setWhatsappDayEndReport(e.target.checked)}
                          disabled={!whatsappEnabled}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' } }}
                        />
                      }
                      label={<Typography sx={{ color: '#FFFFFF' }}>Daily End Report</Typography>}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  WhatsApp API Configuration
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="API URL"
                      value={whatsappApiUrl}
                      onChange={(e) => setWhatsappApiUrl(e.target.value)}
                      disabled={!whatsappEnabled}
                      placeholder="https://api.whatsapp.com/v1/messages"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="password"
                      label="API Key"
                      value={whatsappApiKey}
                      onChange={(e) => setWhatsappApiKey(e.target.value)}
                      disabled={!whatsappEnabled}
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={whatsappPhoneNumber}
                      onChange={(e) => setWhatsappPhoneNumber(e.target.value)}
                      disabled={!whatsappEnabled}
                      placeholder="+1234567890"
                      InputLabelProps={{ sx: { color: '#D4AF37' } }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
              onClick={saveWhatsappSettings}
              disabled={saving || !whatsappEnabled}
              sx={{
                bgcolor: '#D4AF37',
                color: 'rgb(10, 25, 41)',
                fontWeight: 600,
                px: 4,
                py: 1,
                '&:hover': { bgcolor: '#C19B2E' },
              }}
            >
              {saving ? 'Saving...' : 'Save WhatsApp Settings'}
            </Button>
          </Grid>
        </Grid>
      )}
      
      {/* Timezone Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Timezone Settings
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Select your timezone for accurate time tracking and reporting.
                </Typography>
                
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#D4AF37' }}>Timezone</InputLabel>
                  <Select
                    value={selectedTimezone}
                    label="Timezone"
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                    sx={{
                      color: '#FFFFFF',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                      '& .MuiSvgIcon-root': { color: '#D4AF37' }
                    }}
                  >
                    {TIMEZONES.map((tz) => (
                      <MenuItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
              onClick={saveTimezoneSettings}
              disabled={saving}
              sx={{
                bgcolor: '#D4AF37',
                color: 'rgb(10, 25, 41)',
                fontWeight: 600,
                px: 4,
                py: 1,
                '&:hover': { bgcolor: '#C19B2E' },
              }}
            >
              {saving ? 'Saving...' : 'Save Timezone'}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Work Schedule Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>

        </Grid>
      )}

      {/* Overtime & Attendance Tab */}
      {tabValue === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Overtime Configuration
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Configure overtime calculation rules and limits.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={overtimeEnabled}
                      onChange={(e) => setOvertimeEnabled(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Enable Overtime Calculation</Typography>}
                />
                
                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />
                
                <TextField
                  fullWidth
                  type="number"
                  label="Overtime Rate (Multiplier)"
                  value={overtimeRate}
                  onChange={(e) => setOvertimeRate(parseFloat(e.target.value) || 1.5)}
                  inputProps={{ min: 1, max: 3, step: 0.1 }}
                  disabled={!overtimeEnabled}
                  helperText="e.g., 1.5 for 1.5x normal rate"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    },
                    '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                  }}
                />
                
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Overtime Hours"
                  value={minimumOvertimeHours}
                  onChange={(e) => setMinimumOvertimeHours(parseFloat(e.target.value) || 1)}
                  inputProps={{ min: 0.5, max: 8, step: 0.5 }}
                  disabled={!overtimeEnabled}
                  helperText="Minimum hours to qualify for overtime"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    },
                    '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                  }}
                />
                
                <TextField
                  fullWidth
                  type="number"
                  label="Daily Overtime Limit (Hours)"
                  value={dailyOvertimeLimit}
                  onChange={(e) => setDailyOvertimeLimit(parseFloat(e.target.value) || 4)}
                  inputProps={{ min: 1, max: 12 }}
                  disabled={!overtimeEnabled}
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  type="number"
                  label="Weekly Overtime Limit (Hours)"
                  value={weeklyOvertimeLimit}
                  onChange={(e) => setWeeklyOvertimeLimit(parseFloat(e.target.value) || 20)}
                  inputProps={{ min: 1, max: 60 }}
                  disabled={!overtimeEnabled}
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={{ color: '#D4AF37' }}>Overtime Calculation Method</InputLabel>
                  <Select
                    value={overtimeCalculationMethod}
                    label="Overtime Calculation Method"
                    onChange={(e) => setOvertimeCalculationMethod(e.target.value)}
                    disabled={!overtimeEnabled}
                    sx={{
                      color: '#FFFFFF',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                      '& .MuiSvgIcon-root': { color: '#D4AF37' }
                    }}
                  >
                    <MenuItem value="daily">Daily (After daily hours)</MenuItem>
                    <MenuItem value="weekly">Weekly (After weekly hours)</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Late Arrival & Early Departure
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Configure grace periods and deduction rules.
                </Typography>
                
                <TextField
                  fullWidth
                  type="number"
                  label="Late Arrival Grace Period (Minutes)"
                  value={lateArrivalGracePeriod}
                  onChange={(e) => setLateArrivalGracePeriod(parseInt(e.target.value) || 15)}
                  inputProps={{ min: 0, max: 60 }}
                  helperText="Grace period before marking as late"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    },
                    '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                  }}
                />
                
                <TextField
                  fullWidth
                  type="number"
                  label="Early Departure Grace Period (Minutes)"
                  value={earlyDepartureGracePeriod}
                  onChange={(e) => setEarlyDepartureGracePeriod(parseInt(e.target.value) || 15)}
                  inputProps={{ min: 0, max: 60 }}
                  helperText="Grace period before marking as early departure"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    },
                    '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                  }}
                />
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={lateArrivalDeduction}
                      onChange={(e) => setLateArrivalDeduction(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Deduct for Late Arrival</Typography>}
                />
                
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#B4C4A8' }}>
                  When enabled, late arrivals will be deducted from salary.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={earlyDepartureDeduction}
                      onChange={(e) => setEarlyDepartureDeduction(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Deduct for Early Departure</Typography>}
                />
                
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#B4C4A8' }}>
                  When enabled, early departures will be deducted from salary.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Break Time Configuration
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Configure break time limits and deductions.
                </Typography>
                
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Break Duration (Minutes)"
                  value={maxBreakDuration}
                  onChange={(e) => setMaxBreakDuration(parseInt(e.target.value) || 60)}
                  inputProps={{ min: 15, max: 180 }}
                  helperText="Maximum allowed break time per day"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    },
                    '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                  }}
                />
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={breakTimeDeduction}
                      onChange={(e) => setBreakTimeDeduction(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Deduct Break Time from Working Hours</Typography>}
                />
                
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#B4C4A8' }}>
                  When enabled, break time will be deducted from total working hours.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Face Verification
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Configure face verification for attendance.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={faceVerificationEnabled}
                      onChange={(e) => setFaceVerificationEnabled(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Enable Face Verification</Typography>}
                />
                
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#B4C4A8' }}>
                  When enabled, employees can use face recognition for attendance.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={faceVerificationRequired}
                      onChange={(e) => setFaceVerificationRequired(e.target.checked)}
                      disabled={!faceVerificationEnabled}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Require Face Verification for All Attendance</Typography>}
                />
                
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#B4C4A8' }}>
                  When enabled, face verification will be mandatory for login/logout.
                </Typography>
                
                <TextField
                  fullWidth
                  type="number"
                  label="Face Verification Tolerance"
                  value={faceVerificationTolerance}
                  onChange={(e) => setFaceVerificationTolerance(parseFloat(e.target.value) || 0.6)}
                  inputProps={{ min: 0.1, max: 1, step: 0.1 }}
                  disabled={!faceVerificationEnabled}
                  helperText="Lower value = stricter matching (0.1 to 1.0)"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    },
                    '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
                onClick={saveOvertimeSettings}
                disabled={saving || !overtimeEnabled}
                sx={{
                  bgcolor: '#D4AF37',
                  color: 'rgb(10, 25, 41)',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: '#C19B2E' },
                }}
              >
                {saving ? 'Saving...' : 'Save Overtime Settings'}
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
                onClick={saveAttendanceSettings}
                disabled={saving}
                sx={{
                  bgcolor: '#D4AF37',
                  color: 'rgb(10, 25, 41)',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: '#C19B2E' },
                }}
              >
                {saving ? 'Saving...' : 'Save Attendance Settings'}
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
                onClick={saveBreakSettings}
                disabled={saving}
                sx={{
                  bgcolor: '#D4AF37',
                  color: 'rgb(10, 25, 41)',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: '#C19B2E' },
                }}
              >
                {saving ? 'Saving...' : 'Save Break Settings'}
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
                onClick={saveFaceVerificationSettings}
                disabled={saving || !faceVerificationEnabled}
                sx={{
                  bgcolor: '#D4AF37',
                  color: 'rgb(10, 25, 41)',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: '#C19B2E' },
                }}
              >
                {saving ? 'Saving...' : 'Save Face Settings'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      )}

      {/* Logo Settings Tab */}
      {tabValue === 5 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Company Logo
                </Typography>

                {currentLogo && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#D4AF37' }}>
                      Current Logo
                    </Typography>
                    <img
                      src={currentLogo}
                      alt="Current Logo"
                      className="settings-logo-preview"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<Delete />}
                      onClick={deleteLogo}
                      disabled={logoLoading}
                      sx={{ 
                        borderColor: '#D4AF37',
                        color: '#D4AF37',
                        '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' }
                      }}
                    >
                      Delete Logo
                    </Button>
                  </Box>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#D4AF37' }}>
                    Upload New Logo
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={logoLoading}
                    className="hidden-input"
                    ref={fileInputRef}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadFile />}
                    onClick={() => fileInputRef.current.click()}
                    disabled={logoLoading}
                    sx={{ 
                      borderColor: '#D4AF37',
                      color: '#D4AF37',
                      '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' }
                    }}
                  >
                    {logoFile ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                </Box>

                {logoPreview && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#D4AF37' }}>
                      Logo Preview
                    </Typography>
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      className="settings-logo-preview"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Logo Guidelines
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 500 }}>
                  Recommended specifications:
                </Typography>
                <ul className="settings-list">
                  <li>Format: PNG, JPG, or JPEG</li>
                  <li>Dimensions: 150x75 pixels (minimum)</li>
                  <li>File size: Under 500KB</li>
                  <li>Background: Transparent or solid color</li>
                </ul>
                <Typography variant="body2" sx={{ mt: 2, color: '#B4C4A8' }}>
                  The logo will be displayed in the header bar and will replace the default "Smart Attendance" text.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={logoLoading ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
              onClick={saveLogo}
              disabled={!logoFile || logoLoading}
              sx={{
                bgcolor: '#D4AF37',
                color: 'rgb(10, 25, 41)',
                fontWeight: 600,
                px: 4,
                py: 1,
                '&:hover': { bgcolor: '#C19B2E' },
              }}
            >
              {logoLoading ? 'Uploading...' : 'Save Logo'}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Google Sheets Integration Tab */}
      {tabValue === 6 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Google Sheets Integration
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Connect your Google Sheet to automatically sync attendance and payslip data.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={googleSheetsEnabled}
                      onChange={(e) => setGoogleSheetsEnabled(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Enable Google Sheets Sync</Typography>}
                />
                
                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />
                
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Configuration
                </Typography>
                
                <TextField
                  fullWidth
                  label="Google Sheet URL"
                  value={googleSheetUrl}
                  onChange={(e) => setGoogleSheetUrl(e.target.value)}
                  disabled={!googleSheetsEnabled}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Apps Script Web App URL"
                  value={googleAppsScriptUrl}
                  onChange={(e) => setGoogleAppsScriptUrl(e.target.value)}
                  disabled={!googleSheetsEnabled}
                  placeholder="https://script.google.com/a/macros/.../exec"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Attendance Sheet ID"
                  value={googleSheetId}
                  onChange={(e) => setGoogleSheetId(e.target.value)}
                  disabled={!googleSheetsEnabled}
                  placeholder="Enter Attendance Sheet ID"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Payslip Sheet ID"
                  value={googlePayslipSheetId}
                  onChange={(e) => setGooglePayslipSheetId(e.target.value)}
                  disabled={!googleSheetsEnabled}
                  placeholder="Enter Payslip Sheet ID (separate Google Sheet)"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Service Account Email"
                  value={googleServiceAccountEmail}
                  onChange={(e) => setGoogleServiceAccountEmail(e.target.value)}
                  disabled={!googleSheetsEnabled}
                  placeholder="service-account@project.iam.gserviceaccount.com"
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
<TextField
                   fullWidth
                   label="Private Key"
                   value={googlePrivateKey}
                   onChange={(e) => setGooglePrivateKey(e.target.value)}
                   disabled={!googleSheetsEnabled}
                   placeholder="-----BEGIN PRIVATE KEY-----&#10;...Paste the entire key from your JSON file..."
                   multiline
                   rows={4}
                   helperText="Paste the entire private key from your Google Service Account JSON file. Must include BEGIN PRIVATE KEY and END PRIVATE KEY headers."
                   InputLabelProps={{ sx: { color: '#D4AF37' } }}
                   sx={{ 
                     mb: 2,
                     '& .MuiInputLabel-root': { color: '#D4AF37' },
                     '& .MuiOutlinedInput-root': {
                       color: '#FFFFFF',
                       '& fieldset': { borderColor: '#D4AF37' },
                       '&:hover fieldset': { borderColor: '#D4AF37' },
                       '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                     },
                     '& .MuiFormHelperText-root': { color: '#B4C4A8' }
                   }}
                 />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Sync Settings
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: '#B4C4A8' }}>
                  Configure automatic data sync to Google Sheets.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={dailyAttendanceSync}
                      onChange={(e) => setDailyAttendanceSync(e.target.checked)}
                      disabled={!googleSheetsEnabled}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Daily Attendance Report</Typography>}
                />
                <Typography variant="body2" sx={{ mb: 2, ml: 4, color: '#B4C4A8' }}>
                  Sync attendance data to Google Sheets every day
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={monthlyPayslipSync}
                      onChange={(e) => setMonthlyPayslipSync(e.target.checked)}
                      disabled={!googleSheetsEnabled}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Monthly Payslip Report</Typography>}
                />
                <Typography variant="body2" sx={{ mb: 2, ml: 4, color: '#B4C4A8' }}>
                  Sync payslip data to Google Sheets on 1st of each month
                </Typography>
                
                <TextField
                  fullWidth
                  type="time"
                  label="Daily Sync Time"
                  value={syncTime}
                  onChange={(e) => setSyncTime(e.target.value)}
                  disabled={!googleSheetsEnabled || !dailyAttendanceSync}
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 3,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />
                
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Real-time Sync
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={realtimeSync}
                      onChange={(e) => setRealtimeSync(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Real-time Attendance Sync</Typography>}
                />
                <Typography variant="body2" sx={{ mb: 2, ml: 4, color: '#B4C4A8' }}>
                  Sync attendance to Google Sheets immediately on login, logout, and break events
                </Typography>
                
                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />
                
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Auto Absent Marking
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={autoAbsentEnabled}
                      onChange={(e) => setAutoAbsentEnabled(e.target.checked)}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Auto Mark Absent</Typography>}
                />
                <Typography variant="body2" sx={{ mb: 1, ml: 4, color: '#B4C4A8' }}>
                  Automatically mark employees as absent if they haven't logged in
                </Typography>
                
                <TextField
                  fullWidth
                  type="time"
                  label="Auto Absent Time"
                  value={autoAbsentTime}
                  onChange={(e) => setAutoAbsentTime(e.target.value)}
                  disabled={!googleSheetsEnabled || !autoAbsentEnabled}
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{ 
                    mb: 3,
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
                
                {lastSyncStatus && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(212,175,55,0.1)', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1 }}>
                      Last Sync Status
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      {lastSyncStatus.type} - {lastSyncStatus.status} - {new Date(lastSyncStatus.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 2, bgcolor: '#D4AF37', opacity: 0.3 }} />
                
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  Manual Sync
                </Typography>
                
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={20} /> : <Refresh />}
                    onClick={() => handleManualSync('attendance')}
                    disabled={syncing || !googleSheetsEnabled}
                    sx={{
                      borderColor: '#D4AF37',
                      color: '#D4AF37',
                      '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' }
                    }}
                  >
                    Sync Attendance Now
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={20} /> : <Refresh />}
                    onClick={() => handleManualSync('payslip')}
                    disabled={syncing || !googleSheetsEnabled}
                    sx={{
                      borderColor: '#D4AF37',
                      color: '#D4AF37',
                      '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' }
                    }}
                  >
                    Sync Payslip Now
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'rgb(10, 25, 41)' }} /> : <Save />}
                onClick={saveGoogleSheetsSettings}
                disabled={saving || !googleSheetsEnabled}
                sx={{
                  bgcolor: '#D4AF37',
                  color: 'rgb(10, 25, 41)',
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: '#C19B2E' },
                }}
              >
                {saving ? 'Saving...' : 'Save Google Sheets Settings'}
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  How to Set Up Google Sheets Integration
                </Typography>
                <ol className="settings-list-lineheight">
                  <li>Create a new Google Sheet or use an existing one</li>
                  <li>Go to Google Cloud Console and create a service account</li>
                  <li>Download the JSON key file containing the private key</li>
                  <li>Share your Google Sheet with the service account email (with Editor access)</li>
                  <li>Copy the Sheet ID from the Google Sheet URL (between /d/ and /edit)</li>
                  <li>Paste the service account email and private key in the settings above</li>
                </ol>
                <Typography variant="body2" sx={{ mt: 2, color: '#D4AF37' }}>
                  <strong>Note:</strong> The Google Sheet should have two separate sheets named "Attendance" and "Payslips" for data to be synced correctly.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Settings;